import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createFlowInAPI, fetchFlowsFromAPI } from "../../Redux/FlowDucks";
import { fetchBusinessesFromAPI } from "../../Redux/BusinessDucks";
import {
  Box, Typography, List, ListItem, ListItemButton, ListItemText, TextField, Button,
  Select, MenuItem, InputLabel, FormControl, Modal
} from "@mui/material";
import { Add, AddCircleOutline } from "@mui/icons-material";

export function FlowList({ onFlowSelect }) {
  const dispatch = useDispatch();
  const flows = useSelector((store) => store.flow.flowList); // Lista de flujos desde Redux
  const businessLineList = useSelector(store => store.business);

  // Filtro de la lista
  const [selectedBusinessLine, setSelectedBusinessLine] = useState("0");

  // Creación (modales)
  const [step1Open, setStep1Open] = useState(false);   // modal con la opción "Crear nuevo flujo"
  const [step2Open, setStep2Open] = useState(false);   // modal con línea de negocio + nombre
  const [createName, setCreateName] = useState("");
  const [createBusinessLine, setCreateBusinessLine] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    dispatch(fetchFlowsFromAPI("0"));
    dispatch(fetchBusinessesFromAPI());
  }, []);

  const handleChangeOption = (event) => {
    const { value } = event.target;
    setSelectedBusinessLine(value);
    dispatch(fetchFlowsFromAPI(value));
  };

  const openCreateWizard = () => {
    setCreateName("");
    setCreateBusinessLine("");
    setStep1Open(true);
  };

  const goToDetails = () => {
    setStep1Open(false);
    setStep2Open(true);
  };

  const confirmCreate = async () => {
    if (!createName.trim()) return;
    const businessLineIdNum = Number(createBusinessLine);
    if (!businessLineIdNum || businessLineIdNum <= 0) return;

    setCreating(true);
    try {
      const newFlow = {
        name: createName.trim(),
        content: '{"nodes":[],"edges":[]}',
        businessLineId: businessLineIdNum,
      };
      const created = await dispatch(createFlowInAPI(newFlow));
      setStep2Open(false);
      setCreateName("");
      setCreateBusinessLine("");
      if (created && created.id) {
        onFlowSelect(created);
      } else {
        dispatch(fetchFlowsFromAPI(selectedBusinessLine));
      }
    } catch (err) {
      console.error("Error creando flow:", err);
      alert("No se pudo crear el flujo. Revisa la consola/red.");
    } finally {
      setCreating(false);
    }
  };

  const modalBoxSx = {
    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
    width: 460, bgcolor: "background.paper", borderRadius: 2, boxShadow: 24, p: 3,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Flujos Whatsapp
      </Typography>

      <List sx={{ flexGrow: 1, overflow: "auto", mb: 2 }}>
        {flows.map((flow) => (
          <ListItem key={flow.id} disablePadding sx={{ borderRadius: 1 }}>
            <ListItemButton
              onClick={() => onFlowSelect(flow)}
              sx={{ borderRadius: 1, "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.06)" } }}
            >
              <ListItemText primary={flow.name} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Filtro por línea de negocio */}
      <FormControl variant="outlined" className="apple-select" sx={{ mb: "8px" }} fullWidth size="small">
        <InputLabel id="business-line-label">Filtrar por línea</InputLabel>
        <Select
          labelId="business-line-label"
          label="Filtrar por línea"
          value={selectedBusinessLine}
          onChange={handleChangeOption}
        >
          <MenuItem value={"0"}>Todos</MenuItem>
          {businessLineList.map((option) => (
            <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button variant="contained" startIcon={<Add />} onClick={openCreateWizard} fullWidth>
        Nuevo flujo
      </Button>

      {/* Modal 1: opción Crear nuevo flujo */}
      <Modal open={step1Open} onClose={() => setStep1Open(false)}>
        <Box sx={modalBoxSx}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Nuevo flujo</Typography>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<AddCircleOutline />}
            sx={{ justifyContent: "flex-start", p: 2, textAlign: "left" }}
            onClick={goToDetails}
          >
            Crear nuevo flujo
          </Button>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button onClick={() => setStep1Open(false)}>Cancelar</Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal 2: línea de negocio + nombre */}
      <Modal open={step2Open} onClose={() => !creating && setStep2Open(false)}>
        <Box sx={modalBoxSx}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Crear nuevo flujo</Typography>
          <FormControl variant="outlined" fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="create-bl-label">Línea de negocio</InputLabel>
            <Select
              labelId="create-bl-label"
              label="Línea de negocio"
              value={createBusinessLine}
              onChange={(e) => setCreateBusinessLine(e.target.value)}
              disabled={creating}
            >
              {businessLineList.map((option) => (
                <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Nombre del flujo"
            placeholder="Ej: Atención inicial laboral"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && confirmCreate()}
            fullWidth
            size="small"
            disabled={creating}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
            <Button onClick={() => setStep2Open(false)} disabled={creating}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={confirmCreate}
              disabled={creating || !createName.trim() || !Number(createBusinessLine)}
            >
              {creating ? "Creando…" : "Crear"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
